import React from "react";
import { X, Info } from "lucide-react";
import CustomModal from "@/shared/components/ui/Modal/CustomModal";
import CommonAvatar from "@/shared/components/ui/Avatar/CommonAvatar";
import { StatusChip } from "@/shared/components/ui/Status/StatusChip";
import Loader from "@/shared/components/ui/Loaders/Loader";
import { useRelationshipMappingDetail } from "../../hooks/useAccountRelationship";

const normalizeBulletItems = (items = []) => {
  const raw = Array.isArray(items) ? items : [items];
  return raw
    .flatMap((item) =>
      String(item || "")
        .split(/\r?\n/)
        .map((line) => line.replace(/^[-•]\s*/, "").trim()),
    )
    .filter(Boolean);
};

const BulletList = ({ items = [], className = "" }) => {
  const normalizedItems = normalizeBulletItems(items);

  if (!normalizedItems.length) {
    return <p className="text-xs text-gray-400">—</p>;
  }

  return (
    <ul
      className={`space-y-1.5 text-xs text-gray-600 text-wrap break-words ${className}`}
    >
      {normalizedItems.map((line, i) => (
        <li key={i} className="flex items-start gap-2">
          <span
            aria-hidden="true"
            className="mt-[0.45em] h-1 w-1 shrink-0 rounded-full bg-gray-500"
          />
          <span className="min-w-0 flex-1">{line}</span>
        </li>
      ))}
    </ul>
  );
};

const SectionTitle = ({ children }) => (
  <h3 className="text-xs font-semibold text-gray-800 mb-2">{children}</h3>
);

const strengthVariant = (strength) => {
  const s = String(strength || "").toLowerCase();
  if (s.includes("strong")) return "success";
  if (s.includes("medium")) return "warning";
  if (s.includes("weak")) return "danger";
  return "neutral";
};

const ProspectProfileModal = ({
  isOpen,
  onClose,
  userId,
  relationshipMappingId,
}) => {
  const {
    data: profile,
    isLoading,
    isError,
    error,
  } = useRelationshipMappingDetail({
    relationshipMappingId,
    userId,
    enabled: isOpen,
  });

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      width="min(960px, 96vw)"
      className="items-stretch flex flex-col p-5"
    >
      <div className="relative w-full mb-4 flex justify-between items-center">
        <p className="text-[0.85rem] font-bold tracking-wider text-gray-800 uppercase">
          Prospect profile
        </p>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-800"
          aria-label="Close"
        >
          <X size={20} />
        </button>
      </div>
      <div className="flex flex-col w-full h-[75dvh] overflow-y-auto">
        {isLoading && (
          <div className="flex items-center justify-center min-h-[320px] w-full">
            <Loader text="Loading prospect profile..." />
          </div>
        )}

        {isError && (
          <div className="p-8 text-center text-sm text-error-500">
            {(() => {
              const d = error?.response?.data?.detail;
              if (Array.isArray(d))
                return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
              return (
                d ||
                error?.response?.data?.message ||
                error?.message ||
                "Failed to load profile."
              );
            })()}
          </div>
        )}

        {!isLoading && !isError && profile && (
          <div className="flex flex-col gap-5 w-full px-1">
            {/* Header */}
            <div className="flex flex-col sm:flex-row gap-4 sm:items-start pr-10">
              <CommonAvatar
                imageUrl={profile.profile_image}
                name={profile.person_name}
                size="xl"
              />
              <div className="flex-1 min-w-0 space-y-1.5">
                <h2 className="text-xl font-bold text-secondary">
                  {profile.person_name}
                </h2>
                {profile.linkedin_url ? (
                  <a
                    href={profile.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-secondary underline break-all"
                  >
                    LinkedIn profile
                  </a>
                ) : null}
                <p className="text-xs text-gray-500">
                  {/* <span className="text-gray-800 font-medium">
                    CXO Influence Score:
                  </span>{" "}
                  <span className="text-gray-800 font-medium">
                    {profile.cxo_influence_score ?? "—"}
                  </span>{" "}
                  <div className="inline-block">|</div>{" "} */}
                  <StatusChip
                    label={profile.influence_level || "—"}
                    variant="success"
                  />
                </p>
                <p className="text-xs text-gray-800 font-medium">
                  {/* {profile.relationship_type || "Relationship"}
                  <div className="inline-block text-gray-500 mx-0.5">
                    |
                  </div>{" "} */}
                  <span className="text-gray-800 font-medium text-wrap break-words">
                    {profile.domain_focus || "—"}
                  </span>
                </p>
              </div>
            </div>

            {/* Why this contact matters */}
            {profile.why_this_contact_matters ? (
              <div className="rounded-xl bg-[#E8F4F7] border border-secondary/20 px-4 py-3 flex gap-3">
                <Info
                  className="shrink-0 text-secondary mt-0.5"
                  size={18}
                  strokeWidth={2}
                />
                <div>
                  <p className="text-xs font-semibold text-gray-900 mb-1">
                    Why this contact matters
                  </p>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    {profile.why_this_contact_matters}
                  </p>
                </div>
              </div>
            ) : null}

            {/* Main grid */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
              <div className="lg:col-span-3 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border border-gray-200 rounded-xl p-3">
                    <SectionTitle>Professional Snapshot</SectionTitle>
                    <BulletList items={profile.professional_snapshot} />
                  </div>
                  <div className="border border-gray-200 rounded-xl p-3">
                    <SectionTitle>Likely Priorities</SectionTitle>
                    <BulletList items={profile.likely_priorities} />
                  </div>
                </div>
                <div className="border border-gray-200 rounded-xl p-3">
                  <SectionTitle>Recent Signals</SectionTitle>
                  <BulletList items={profile.recent_signals} />
                </div>
              </div>

              <div className="lg:col-span-2 border border-gray-200 rounded-xl p-4 space-y-4">
                {/* <div>
                  <SectionTitle>Relationship Score</SectionTitle>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-2xl font-bold text-secondary">
                      {profile.relationship_score ?? "—"}
                      <span className="text-sm font-normal text-secondary">
                        /100
                      </span>
                    </span>
                    {profile.relationship_strength ? (
                      <StatusChip
                        label={profile.relationship_strength}
                        variant={strengthVariant(profile.relationship_strength)}
                      />
                    ) : null}
                  </div>
                  {profile.last_interaction_date ? (
                    <p className="text-[0.65rem] text-gray-500 mt-2">
                      <span className="text-gray-800 font-medium">
                        Last interaction:{" "}
                      </span>
                      {profile.last_interaction_date}
                    </p>
                  ) : null}
                </div> */}
                <div>
                  <SectionTitle>Buying role</SectionTitle>
                  <p className="text-xs text-gray-700">
                    {profile.buying_role || "—"}
                  </p>
                </div>
                <div>
                  <SectionTitle>Risk / objection signals</SectionTitle>
                  <BulletList items={profile.risk_objection_signals} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </CustomModal>
  );
};

export default ProspectProfileModal;
